import { useState, useRef } from 'react';
import { readFileInChunks } from '../../../Utils/serviceFile';
import { useQueryClient } from '@tanstack/react-query';
import useFetchWithMsal from "../../useFetchWithMsal";
import { protectedResources } from "../../../authConfig";

export const useInvoiceUploader = () => {
    const [isDragging, setIsDragging] = useState(false);
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState({});
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadStage, setUploadStage] = useState('');

    const fileInputRef = useRef(null);
    const { execute } = useFetchWithMsal({
        scopes: protectedResources.apiScopes.scopes.read,
    });
    const queryClient = useQueryClient();

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files);
        handleFiles(droppedFiles);
    };

    const handleFileInput = (e) => {
        const selectedFiles = Array.from(e.target.files);
        handleFiles(selectedFiles);
    };

    const handleFiles = async (newFiles) => {
        const validFiles = newFiles.filter(file =>
            file.type === 'application/pdf' ||
            file.type === 'image/jpeg' ||
            file.type === 'image/png'
        );

        setFiles(prev => [...prev, ...validFiles]);
        setUploadStatus(null);
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const removeFile = (index) => {
        const fileToRemove = files[index];
        const newPreviews = { ...previews };
        delete newPreviews[fileToRemove.name];
        setPreviews(newPreviews);
        setFiles(prev => prev.filter((_, i) => i !== index));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const simulateUpload = async () => {
        setIsUploading(true);
        setUploadStatus(null);
        setUploadProgress(0);
        setUploadStage('uploading');

        try {
            for (let progress = 0; progress <= 40; progress += 10) {
                setUploadProgress(progress);
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            const fileReadPromises = files.map(file => readFileInChunks(file));
            const fileDataArray = await Promise.all(fileReadPromises);

            setUploadStage('extracting');
            for (let progress = 40; progress <= 70; progress += 10) {
                setUploadProgress(progress);
                await new Promise(resolve => setTimeout(resolve, 120));
            }

            const resultUpload = await execute(
                "POST",
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices`,
                { fileDataArray }
            );

            if (resultUpload.status === 'ERROR') {
                throw new Error(resultUpload.message || 'שגיאה כללית בעיבוד');
            }

            if (resultUpload.results && resultUpload.results.length > 0) {
                const errors = resultUpload.results.filter(result => result.status === 'ERROR');
                const requiresAttentionResults = resultUpload.results.filter(result => result.status === 'REQUIRES_ATTENTION');
                const processingResults = resultUpload.results.filter(result => result.status === 'PROCESSING');
                if (processingResults.length > 0) {
                    setUploadStage('processing');
                    for (let progress = 70; progress <= 90; progress += 10) {
                        setUploadProgress(progress);
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }

                    queryClient.invalidateQueries({ queryKey: ['spInvoices'] });

                    setUploadProgress(100);
                    setUploadStage('completed');

                    setUploadStatus({
                        type: 'PROCESSING',
                        message: 'החשבונית הועלתה בהצלחה ונמצאת בתהליך עיבוד ברקע'
                    });

                    setUploadedFiles(prev => [
                        ...prev,
                        ...(resultUpload.results || []).map(result => ({
                            ...result,
                            fileName: result.fileName || (files.find(f => f.name === result.fileName)?.name) || '',
                            status: result.invoice?.status || result.status,
                            message: result.message,
                            errorDetails: result.invoice?.errorDetails || result.errorDetails,
                            isRetainerInvoice: result.invoice?.isRetainerInvoice || result.isRetainerInvoice
                        }))
                    ]);
                    setFiles([]);

                    setTimeout(() => {
                        if (uploadStatus?.type === 'PROCESSING' || uploadStatus === 'success') {
                            setUploadStage('');
                        }
                    }, 2000);
                    setIsUploading(false);
                    return;
                }
                
                if (errors.length > 0) {
                    const duplicateError = errors.find(error => {
                        if (typeof error.message === 'object' && error.message !== null) {
                            return error.message.stage === 'duplicate_check';
                        }
                        return false;
                    });
                    const companyMismatchError = errors.find(error => {
                        if (typeof error.message === 'object' && error.message !== null) {
                            return error.message.stage === 'company_validation';
                        }
                        return false;
                    });
                    const companyIdMismatchError = errors.find(error => {
                        if (typeof error.message === 'object' && error.message !== null) {
                            return error.message.stage === 'company_id_validation';
                        }
                        return false;
                    });

                    const alreadyProcessedError = errors.find(error => {
                        if (typeof error.message === 'object' && error.message !== null) {
                            return error.message.stage === 'Failed to fetch document from Priority';
                        }
                        return false;
                    });

                    const allocationError = errors.find(error => {
                        if (typeof error.message === 'object' && error.message !== null) {
                            return error.message.stage === 'allocation_validation';
                        }
                        return false;
                    });

                    const transactionInvoiceError = errors.find(error => {
                        const errorMsg = typeof error.message === 'string'
                            ? error.message
                            : (error.message?.message || '');
                        return errorMsg.includes('חשבונית עסקה') ||
                            errorMsg.includes('transaction invoice');
                    });

                    const debitNoteError = errors.find(error => {
                        const errorMsg = typeof error.message === 'string'
                            ? error.message
                            : (error.message?.message || '');
                        return errorMsg.includes('חשבון חיוב') ||
                            errorMsg.includes('debit note');
                    });

                    if (duplicateError) {
                        setUploadStatus('חשבונית זו כבר קיימת במערכת');
                    } else if (transactionInvoiceError) {
                        setUploadStatus('אין אפשרות להעלות חשבונית עסקה למערכת');
                    } else if (debitNoteError) {
                        setUploadStatus('אין אפשרות להעלות חשבון חיוב למערכת');
                    } else if (companyMismatchError) {
                        const errorDetails = companyMismatchError.message;
                        const matchResult = errorDetails.matchResult;
                        setUploadStatus('אי-התאמה בין שם החברה בחשבונית להזמנה - לא ניתן להעלות את החשבונית');
                    } else if (companyIdMismatchError) {
                        const errorDetails = companyIdMismatchError.message;
                        const validationResult = errorDetails.validationResult;
                        const nameMatchResult = errorDetails.nameMatchResult;

                        let errorMessage = 'אי-התאמה בח.פ החברה';
                        if (nameMatchResult && nameMatchResult.isMatch) {
                            errorMessage += `\nשם החברה תואם (${nameMatchResult.confidence}%) אבל ח.פ לא תואם`;
                            errorMessage += `\nח.פ בחשבונית: ${errorDetails.invoiceCompanyId}`;
                            errorMessage += `\nח.פ רשום במערכת: ${errorDetails.registeredCompanyId}`;
                        } else {
                            errorMessage += ' - לא ניתן להעלות את החשבונית';
                        }

                        setUploadStatus(errorMessage);
                    }
                    else if (alreadyProcessedError) {
                        setUploadStatus('חשבונית זו כבר נקלטה בפריוריטי');
                    }
                    else if (allocationError) {
                        const errorDetails = allocationError.message;
                        const validationDetails = errorDetails.validationDetails;
                        let errorMessage = 'דרושה הקצאת תקציב';

                        if (validationDetails && validationDetails.hebrewMessage) {
                            errorMessage = validationDetails.hebrewMessage;
                        } else if (validationDetails && validationDetails.message) {
                            errorMessage = validationDetails.message;
                        }

                        setUploadStatus(errorMessage);
                    } else {
                        const firstError = errors[0];
                        const errorMessage = typeof firstError.message === 'string'
                            ? firstError.message
                            : (firstError.message?.message || 'שגיאה בהעלאת הקבצים');
                        setUploadStatus(errorMessage);
                    }

                    setIsUploading(false);
                    setUploadStage('error');
                    setUploadProgress(0);
                    return;
                }

                if (requiresAttentionResults.length > 0) {
                    setUploadStage('processing');
                    for (let progress = 70; progress <= 90; progress += 10) {
                        setUploadProgress(progress);
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }

                    queryClient.invalidateQueries({ queryKey: ['spInvoices'] });

                    setUploadProgress(100);
                    setUploadStage('completed');

                    const firstResult = requiresAttentionResults[0];
                    const message = firstResult.errorDetails?.hebrewMessage ||
                        firstResult.message ||
                        'חשבונית הועלתה אך דורשת תשומת לב';

                    setUploadStatus({
                        type: 'requires_attention',
                        message: `הועלה בהצלחה: ${message}`
                    });

                    setUploadedFiles(prev => [
                        ...prev,
                        ...(resultUpload.results || []).map(result => ({
                            ...result,
                            fileName: result.fileName || (files.find(f => f.name === result.fileName)?.name) || '',
                            status: result.invoice?.status || result.status,
                            message: result.message,
                            errorDetails: result.invoice?.errorDetails || result.errorDetails,
                            isRetainerInvoice: result.invoice?.isRetainerInvoice || result.isRetainerInvoice
                        }))
                    ]);
                    setFiles([]);

                    setTimeout(() => {
                        if (uploadStatus?.type === 'requires_attention' || uploadStatus === 'success') {
                            setUploadStage('');
                        }
                    }, 1500);
                    setIsUploading(false);
                    return;
                }
            }

            setUploadStage('processing');
            for (let progress = 70; progress <= 90; progress += 10) {
                setUploadProgress(progress);
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            queryClient.invalidateQueries({ queryKey: ['spInvoices'] });

            setUploadProgress(100);
            setUploadStage('completed');
            setUploadStatus('success');

            setUploadedFiles(prev => [
                ...prev,
                ...(resultUpload.results || []).map(result => ({
                    ...result,
                    fileName: result.fileName || (files.find(f => f.name === result.fileName)?.name) || '',
                    status: result.invoice?.status || result.status,
                    message: result.message,
                    errorDetails: result.invoice?.errorDetails || result.errorDetails,
                    isRetainerInvoice: result.invoice?.isRetainerInvoice || result.isRetainerInvoice
                }))
            ]);
            setFiles([]);

        } catch (error) {
            console.error('Upload error:', error);
            setUploadProgress(0);
            setUploadStage('error');

            const errorMessage = error.message || '';

            if (errorMessage.includes('חשבונית עסקה') || errorMessage.includes('transaction invoice')) {
                setUploadStatus('אין אפשרות להעלות חשבונית עסקה למערכת');
            } else if (errorMessage.includes('חשבון חיוב') || errorMessage.includes('debit note')) {
                setUploadStatus('אין אפשרות להעלות חשבון חיוב למערכת');
            } else if (errorMessage.includes('אי-התאמה בשמות החברות')) {
                setUploadStatus(errorMessage);
            } else if (errorMessage.includes('אי-התאמה בח.פ')) {
                setUploadStatus(errorMessage);
            } else if (errorMessage) {
                setUploadStatus(errorMessage);
            } else {
                setUploadStatus('שגיאה בהעלאת הקבצים');
            }

        } finally {
            setTimeout(() => {
                if (uploadStatus === 'success') {
                    setUploadStage('');
                }
            }, 1500);
            setIsUploading(false);
        }
    };

    return {
        isDragging,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
        handleClick,
        fileInputRef,
        handleFileInput,
        files,
        uploadedFiles,
        isUploading,
        simulateUpload,
        uploadStatus,
        removeFile,
        uploadProgress,
        uploadStage
    }
}